<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, invoiceStatusClass, supplierInvoiceStatusLabel } from '../utils/format'
import type { AllocationHistoryRow, SupplierInvoice } from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const { getSupplierInvoice, listAllocationHistory, voidSupplierInvoice } = useFinanceApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canUpdate = computed(() => permissions.value.includes('supplier_invoices.update'))
const canPayments = computed(() => permissions.value.includes('payments.view'))
const canCreatePayment = computed(() => permissions.value.includes('payments.create'))

const invoiceId = computed(() => String(route.params.id ?? ''))
const invoice = ref<SupplierInvoice | null>(null)
const allocations = ref<AllocationHistoryRow[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const allocationsError = ref('')
const activeTab = ref<'summary' | 'allocations'>('summary')

const paidAmount = computed(() =>
  allocations.value
    .filter((allocation) => allocation.allocation_status === 'active')
    .reduce((sum, allocation) => sum + Number(allocation.allocated_amount ?? 0), 0),
)

const outstandingAmount = computed(() =>
  Number(invoice.value?.invoice_amount ?? 0) - paidAmount.value,
)

async function load() {
  loading.value = true
  error.value = ''
  allocationsError.value = ''
  try {
    invoice.value = await getSupplierInvoice(invoiceId.value)
  } catch (cause) {
    error.value = formatApiError(cause)
    loading.value = false
    return
  }
  loading.value = false

  if (!canPayments.value) return
  try {
    allocations.value = await listAllocationHistory('supplier', invoiceId.value)
  } catch (cause) {
    allocationsError.value = formatApiError(cause)
  }
}

async function voidInvoice() {
  if (!invoice.value) return
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('finance.payables.voidConfirm'))) return
  try {
    await voidSupplierInvoice(invoice.value.id)
    actionMessage.value = t('finance.payables.voided')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/payables/invoices">
      ← {{ t('finance.payables.supplierInvoicesTitle') }}
    </RouterLink>
    <h2>{{ invoice?.invoice_number || t('finance.payables.supplierInvoicesTitle') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else-if="invoice">
    <div class="toolbar">
      <div class="button-group">
        <RouterLink
          v-if="canCreatePayment && invoice.status !== 'void'"
          class="button-group__item is-primary"
          :to="`/payables/payments/new?agent_id=${invoice.supplier_agent_id}&invoice_id=${invoice.id}&amount=${outstandingAmount}`"
        >
          {{ t('finance.payments.newPayment') }}
        </RouterLink>
        <RouterLink
          v-if="canUpdate && invoice.status !== 'void'"
          class="button-group__item"
          :to="`/payables/invoices/${invoice.id}/edit`"
        >
          {{ t('finance.common.edit') }}
        </RouterLink>
        <button
          v-if="canUpdate && invoice.status !== 'void'"
          class="button-group__item is-danger"
          @click="voidInvoice"
        >
          {{ t('finance.payables.void') }}
        </button>
        <button class="button-group__item" :disabled="loading" @click="load">
          {{ t('finance.common.refresh') }}
        </button>
      </div>
      <span class="spacer" />
      <div class="summary">
        <span class="status-badge" :class="invoiceStatusClass(invoice.status)">
          {{ supplierInvoiceStatusLabel(t, invoice.status) }}
        </span>
        <span>{{ invoice.supplier_name || invoice.supplier_agent_id }}</span>
      </div>
    </div>

    <p v-if="actionError" class="error-inline">{{ actionError }}</p>
    <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.common.amount') }}</span>
        <span class="summary-value">
          {{ formatMoney(invoice.invoice_amount, invoice.currency_code) }}
        </span>
        <span class="summary-label">{{ formatDisplayDate(invoice.invoice_date) }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.receivables.paid') }}</span>
        <span class="summary-value">{{ formatMoney(paidAmount, invoice.currency_code) }}</span>
        <span class="summary-label">{{ t('finance.common.exchangeRate') }}: {{ invoice.exchange_rate }}</span>
      </div>
      <div class="summary-card" :class="{ attention: outstandingAmount > 0 }">
        <span class="summary-label">{{ t('finance.receivables.outstanding') }}</span>
        <span class="summary-value">{{ formatMoney(outstandingAmount, invoice.currency_code) }}</span>
        <span class="summary-label">
          {{ t('finance.common.dueDate') }}: {{ formatDisplayDate(invoice.due_date) }}
        </span>
      </div>
    </div>

    <div class="tabs">
      <button
        class="tab-button"
        :class="{ 'is-active': activeTab === 'summary' }"
        @click="activeTab = 'summary'"
      >
        {{ t('finance.common.detail') }}
      </button>
      <button
        class="tab-button"
        :class="{ 'is-active': activeTab === 'allocations' }"
        @click="activeTab = 'allocations'"
      >
        {{ t('finance.receivables.allocations') }}
      </button>
    </div>

    <div class="tab-block">
      <div v-if="activeTab === 'summary'" class="panel">
        <table class="data-table compact">
          <tbody>
            <tr>
              <td class="field-label">{{ t('finance.payables.supplier') }}</td>
              <td class="field-value">{{ invoice.supplier_name || invoice.supplier_agent_id }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.payables.invoiceNumber') }}</td>
              <td class="field-value">{{ invoice.invoice_number }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.payables.invoiceDate') }}</td>
              <td class="field-value">{{ formatDisplayDate(invoice.invoice_date) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.common.dueDate') }}</td>
              <td class="field-value">{{ formatDisplayDate(invoice.due_date) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.common.currency') }}</td>
              <td class="field-value">{{ invoice.currency_code }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.payables.purchaseOrder') }}</td>
              <td class="field-value">{{ invoice.purchase_order_id ? `#${invoice.purchase_order_id}` : '—' }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.payables.department') }}</td>
              <td class="field-value">{{ invoice.department_name || '—' }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.payables.warehouse') }}</td>
              <td class="field-value">{{ invoice.warehouse_name || '—' }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.common.notes') }}</td>
              <td class="field-value">{{ invoice.notes || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="panel">
        <p v-if="allocationsError" class="error-inline">{{ allocationsError }}</p>
        <div v-else-if="allocations.length" class="table-scroll">
          <table class="data-table compact">
            <thead>
              <tr>
                <th>{{ t('finance.receivables.allocationDate') }}</th>
                <th>{{ t('finance.common.payments') }}</th>
                <th>{{ t('finance.common.reference') }}</th>
                <th class="cell-num">{{ t('finance.receivables.allocatedAmount') }}</th>
                <th>{{ t('finance.common.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="allocation in allocations" :key="allocation.id">
                <td class="cell-date">{{ formatDisplayDate(allocation.allocation_date) }}</td>
                <td class="cell-code">#{{ allocation.payment_id }}</td>
                <td>{{ allocation.payment_reference || '—' }}</td>
                <td class="cell-num">
                  {{ formatMoney(allocation.allocated_amount, allocation.currency_code) }}
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="allocation.allocation_status === 'active' ? 'status-ok' : 'status-critical'"
                  >
                    {{ allocation.allocation_status === 'active'
                      ? t('finance.common.active')
                      : t('finance.adjustmentStatus.reversed') }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-inline">
          {{ canPayments ? t('finance.payables.noAllocations') : t('finance.common.noPermission') }}
        </p>
      </div>
    </div>
  </template>
</template>
